import { ChangeEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Resolver, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';

import ReorderWrapper from '@/components/atoms/ReorderWrapper/ReorderWrapper';
import ToolkitAccordionWrapper from '@/components/organisms/Toolkit/ToolkitAccordionWrapper';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import useDialogAlert from '@/hooks/useDialogAlert';
import { selectNodeIdByActionId } from '@/redux/dialogs/selectors';
import { RootState } from '@/shared/types/state';
import { capitalizeFirstLetter } from '@/util/util';
import { optionSchema } from '@/util/validation/schemas/dialog-schemas';

type FormType = { label: string; text: string };

type Props = {
  actionId: string;
  option: { label: string; text: string; option_id: string };
  onChange: (value: { [key: string]: string }, index: number) => void;
  onDelete: (index: number) => void;
  index: number;
  handleDragItem: (dragIndex: number, hoverIndex: number) => void;
  getIndex: (optionId: string) => number;
  onDrop: () => void;
};

const ToolkitButton = memo(
  ({ actionId, option, onChange, onDelete, index, handleDragItem, getIndex, onDrop }: Props) => {
    const { t } = useTranslation();
    const formMethods = useForm<FormType>({
      mode: 'onChange',
      defaultValues: { label: option.label ?? '', text: option.text ?? '' },
      resolver: yupResolver(optionSchema) as Resolver<FormType>,
    });
    const {
      formState: { errors, isDirty },
      control,
      trigger,
    } = formMethods;

    const parentNodeId = useSelector(selectNodeIdByActionId(actionId));

    const { setFieldError } = useDialogAlert({
      id: actionId,
      nodeId: parentNodeId,
      title: t('actions.types.text'),
      type: 'text',
    });

    const optionLabelText = useWatch({ control, name: 'label' });
    const optionTextText = useWatch({ control, name: 'text' });

    const errorLabelRef = useRef(false);
    const errorTextRef = useRef(false);

    const labelErrorMessage = useMemo(
      () => capitalizeFirstLetter(errors?.label?.message),
      [errors?.label?.message]
    );
    const textErrorMessage = useMemo(
      () => capitalizeFirstLetter(errors?.text?.message),
      [errors?.text?.message]
    );
    const hasError = !!errors?.label?.message || !!errors?.text?.message;

    const { optionOpen } = useSelector(
      (state: RootState) => ({ optionOpen: state.nodes.selectedOptionIndex === index }),
      shallowEqual
    );

    const defaultOpenState = optionOpen || hasError || isDirty;
    const [isOpen, setIsOpen] = useState(defaultOpenState);

    useEffect(() => {
      setIsOpen(defaultOpenState);
    }, [defaultOpenState]);

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        onChange({ [name]: value }, index);
      },
      [index, onChange]
    );

    const handleDelete = useCallback(() => {
      onDelete(index);
      setFieldError(`label${index}`, undefined);
      setFieldError(`text${index}`, undefined);
    }, [setFieldError, index, onDelete]);

    const handleOpen = useCallback(() => {
      setIsOpen((prev) => !prev);
    }, []);

    const checkInitialLabelError = useCallback(async () => {
      const isValid = await trigger('label');
      if (labelErrorMessage && !isValid) {
        setFieldError(`label${index}`, labelErrorMessage);
        errorLabelRef.current = true;
      }
    }, [setFieldError, index, labelErrorMessage, trigger]);

    // Dialog errors handlers
    useEffect(() => {
      checkInitialLabelError();
    }, [checkInitialLabelError, trigger]);

    const removeErrorMessage = useCallback(async () => {
      const isValid = await trigger('label');
      if (isValid) {
        setFieldError(`label${index}`, undefined);
        errorLabelRef.current = false;
      }
    }, [setFieldError, index, trigger]);

    useEffect(() => {
      if (errorLabelRef && errorLabelRef.current) {
        removeErrorMessage();
      }
    }, [setFieldError, index, removeErrorMessage, trigger, optionLabelText]);

    const checkInitialTextError = useCallback(async () => {
      const isValid = await trigger('text');
      if (textErrorMessage && !isValid) {
        setFieldError(`text${index}`, textErrorMessage);
        errorTextRef.current = true;
      }
    }, [setFieldError, index, textErrorMessage, trigger]);

    useEffect(() => {
      checkInitialTextError();
    }, [checkInitialTextError, trigger]);

    const removeErrorText = useCallback(async () => {
      const isValid = await trigger('text');
      if (isValid) {
        setFieldError(`text${index}`, undefined);
        errorTextRef.current = false;
      }
    }, [setFieldError, index, trigger]);

    useEffect(() => {
      if (errorTextRef && errorTextRef.current) {
        removeErrorText();
      }
    }, [setFieldError, index, removeErrorText, trigger, optionTextText]);

    return (
      <ReorderWrapper
        dragId="text-button"
        index={index}
        moveItem={handleDragItem}
        getIndex={() => getIndex(option.option_id)}
        placeholderHeight={36}
        dropItem={onDrop}
        canDrag={!isOpen}
      >
        <ToolkitAccordionWrapper
          title={option.label}
          handleDelete={handleDelete}
          onChange={handleOpen}
          expanded={isOpen}
        >
          <Form {...formMethods}>
            <FormField
              control={control}
              name="label"
              render={({ field }) => (
                <FormItem className="mb-6">
                  <FormLabel>{t('common.label')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t('dialog.label_placeholder')}
                      onChange={(event) => {
                        field.onChange(event);
                        handleChange(event);
                      }}
                    />
                  </FormControl>
                  <FormMessage>{labelErrorMessage}</FormMessage>
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="text"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('common.text')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t('dialog.label_placeholder')}
                      onChange={(event) => {
                        field.onChange(event);
                        handleChange(event);
                      }}
                    />
                  </FormControl>
                  <FormMessage>{textErrorMessage}</FormMessage>
                </FormItem>
              )}
            />
          </Form>
        </ToolkitAccordionWrapper>
      </ReorderWrapper>
    );
  }
);

ToolkitButton.displayName = 'ToolkitButton';

export default ToolkitButton;
